/**
 * UsaDatiClienteForm — form produttore pre-compilato dai dati del cliente
 * dell'ordine (Modello C, "Usa i dati del cliente come produttore").
 *
 * Riusato da:
 *  - CambiaProduttoreModal (intento "Usa i dati del cliente");
 *  - GeneraPreventivoOmologaWizard (scorciatoia accanto alla guard produttore
 *    mancante).
 *
 * I campi sono pre-compilati dal cliente (mappatura: email←email,
 * note←referente, tipo_soggetto inferito P.IVA→azienda / solo CF→privato) e
 * restano editabili. Al submit chiama l'endpoint unico
 * POST /richieste/{id}/produttore-da-cliente; è il BE a fare dedup + creare il
 * record e a settare orders.produttore_id.
 */
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  produttoreDaClienteRichiesta,
  type CustomerDetail,
  type ProduttoreDaClientePayload,
  type RichiestaDetail,
} from '../api/richieste';

const inputCls =
  'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const labelCls = 'block text-xs text-ink-soft mb-1';

type TipoSoggetto = '' | 'azienda' | 'privato' | 'ente_pubblico';

function inferTipo(piva: string, cf: string): TipoSoggetto {
  if (piva.trim()) return 'azienda';
  if (cf.trim()) return 'privato';
  return '';
}

interface Props {
  richiestaId: string;
  customer: CustomerDetail;
  onMaterialized: (updated: RichiestaDetail) => void;
  submitLabel?: string;
}

export default function UsaDatiClienteForm({
  richiestaId,
  customer,
  onMaterialized,
  submitLabel,
}: Props) {
  const { t } = useTranslation();
  const effectiveLabel = submitLabel ?? t('richieste.usa_dati_cliente.submit_default');
  // Pre-compilazione dalla mappatura cliente → produttore.
  const [ragioneSociale, setRagioneSociale] = useState(customer.ragione_sociale ?? '');
  const [piva, setPiva] = useState(customer.piva ?? '');
  const [codiceFiscale, setCodiceFiscale] = useState(customer.cf ?? '');
  const [email, setEmail] = useState(customer.email ?? '');
  const [telefono, setTelefono] = useState(customer.telefono ?? '');
  const [indirizzo, setIndirizzo] = useState(customer.indirizzo ?? '');
  const [citta, setCitta] = useState(customer.citta ?? '');
  const [provincia, setProvincia] = useState(customer.provincia ?? '');
  const [cap, setCap] = useState(customer.cap ?? '');
  const [note, setNote] = useState(customer.referente ?? ''); // referente → note
  const [tipoSoggetto, setTipoSoggetto] = useState<TipoSoggetto>(
    inferTipo(customer.piva ?? '', customer.cf ?? ''),
  );

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit() {
    setError(null);
    if (!ragioneSociale.trim()) {
      setError(t('richieste.usa_dati_cliente.val_ragione'));
      return;
    }
    if (!piva.trim() && !codiceFiscale.trim()) {
      setError(t('richieste.usa_dati_cliente.val_piva_cf'));
      return;
    }
    // Override: invia solo i campi valorizzati (gli omessi li deriva il BE).
    const payload: ProduttoreDaClientePayload = {};
    if (ragioneSociale.trim()) payload.ragione_sociale = ragioneSociale.trim();
    if (piva.trim()) payload.piva = piva.trim();
    if (codiceFiscale.trim()) payload.codice_fiscale = codiceFiscale.trim();
    if (email.trim()) payload.email = email.trim();
    if (telefono.trim()) payload.telefono = telefono.trim();
    if (indirizzo.trim()) payload.indirizzo = indirizzo.trim();
    if (citta.trim()) payload.citta = citta.trim();
    if (provincia.trim()) payload.provincia = provincia.trim();
    if (cap.trim()) payload.cap = cap.trim();
    if (note.trim()) payload.note = note.trim();
    if (tipoSoggetto) payload.tipo_soggetto = tipoSoggetto;

    setIsSaving(true);
    try {
      const updated = await produttoreDaClienteRichiesta(richiestaId, payload);
      onMaterialized(updated);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('richieste.usa_dati_cliente.errore_creazione'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-3" data-testid="usa-dati-cliente-form">
      <div className="px-3 py-2 bg-accent-info-bg border border-[#BAE6FD] rounded text-xs text-accent-info">
        <Trans i18nKey="richieste.usa_dati_cliente.avviso" components={{ strong: <strong /> }} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={labelCls}>{t('richieste.usa_dati_cliente.ragione_sociale')}</label>
          <input className={inputCls} value={ragioneSociale} onChange={(e) => setRagioneSociale(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('richieste.usa_dati_cliente.partita_iva')}</label>
          <input className={inputCls} value={piva} maxLength={11} placeholder={t('richieste.usa_dati_cliente.piva_placeholder')}
            onChange={(e) => setPiva(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('richieste.usa_dati_cliente.codice_fiscale')}</label>
          <input className={inputCls} value={codiceFiscale} maxLength={16}
            onChange={(e) => setCodiceFiscale(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('richieste.usa_dati_cliente.email')}</label>
          <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>{t('richieste.usa_dati_cliente.telefono')}</label>
          <input className={inputCls} value={telefono} onChange={(e) => setTelefono(e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className={labelCls}>{t('richieste.usa_dati_cliente.indirizzo')}</label>
          <input className={inputCls} value={indirizzo} onChange={(e) => setIndirizzo(e.target.value)}
            placeholder={t('richieste.usa_dati_cliente.indirizzo_placeholder')} />
        </div>
        <div>
          <label className={labelCls}>{t('richieste.usa_dati_cliente.citta')}</label>
          <input className={inputCls} value={citta} onChange={(e) => setCitta(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={labelCls}>{t('richieste.usa_dati_cliente.provincia')}</label>
            <input className={inputCls} value={provincia} maxLength={2} placeholder={t('richieste.usa_dati_cliente.provincia_placeholder')}
              onChange={(e) => setProvincia(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>{t('richieste.usa_dati_cliente.cap')}</label>
            <input className={inputCls} value={cap} maxLength={5} onChange={(e) => setCap(e.target.value)} />
          </div>
        </div>
        <div>
          <label className={labelCls}>{t('richieste.usa_dati_cliente.tipo_soggetto')}</label>
          <select className={inputCls} value={tipoSoggetto}
            onChange={(e) => setTipoSoggetto(e.target.value as TipoSoggetto)}>
            <option value="">{t('richieste.usa_dati_cliente.tipo_seleziona')}</option>
            <option value="azienda">{t('richieste.usa_dati_cliente.tipo_azienda')}</option>
            <option value="privato">{t('richieste.usa_dati_cliente.tipo_privato')}</option>
            <option value="ente_pubblico">{t('richieste.usa_dati_cliente.tipo_ente')}</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>{t('richieste.usa_dati_cliente.referente')}</label>
          <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <button type="button" onClick={handleSubmit} disabled={isSaving}
          className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60">
          {isSaving ? t('richieste.usa_dati_cliente.creazione_in_corso') : effectiveLabel}
        </button>
      </div>
    </div>
  );
}
